import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AppBar, Icon, type IconName } from '@astral/ui';
import { useT, type TranslationKey } from '../providers/I18nProvider.js';

interface Section {
  key: string;
  icon: IconName;
  items: string[];
}

const SECTIONS: Section[] = [
  { key: 'start', icon: 'sparkles', items: ['whatIs', 'create', 'upload'] },
  { key: 'stickers', icon: 'image', items: ['process', 'reorder', 'replace'] },
  { key: 'emoji', icon: 'sparkles', items: ['single', 'device', 'presets'] },
  { key: 'publish', icon: 'upload', items: ['link', 'progress', 'sync'] },
  { key: 'premium', icon: 'sparkles', items: ['free', 'how', 'methods'] },
  { key: 'more', icon: 'settings', items: ['channels', 'appearance', 'help'] },
];

/** In-app guide: searchable, expandable sections describing real Astral features. */
export function GuideScreen(): JSX.Element {
  const t = useT();
  const navigate = useNavigate();
  const [open, setOpen] = useState<Set<string>>(() => new Set(['start']));
  const [query, setQuery] = useState('');

  const toggle = (key: string): void =>
    setOpen((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const q = query.trim().toLowerCase();
  const results = useMemo(() => {
    if (!q) return null;
    const hits: Array<{ tt: string; dd: string }> = [];
    for (const s of SECTIONS) {
      for (const it of s.items) {
        const tt = t(`guide.${s.key}.${it}T` as TranslationKey);
        const dd = t(`guide.${s.key}.${it}D` as TranslationKey);
        if (`${tt} ${dd}`.toLowerCase().includes(q)) hits.push({ tt, dd });
      }
    }
    return hits;
  }, [q, t]);

  const Item = ({ tt, dd }: { tt: string; dd: string }): JSX.Element => (
    <div className="guide-item">
      <span className="guide-item__dot">
        <Icon name="sparkles" size={12} />
      </span>
      <span className="guide-item__text">
        <span className="guide-item__t">{tt}</span>
        <span className="guide-item__d">{dd}</span>
      </span>
    </div>
  );

  return (
    <div className="app-root">
      <AppBar title={t('guide.title')} onBack={() => navigate(-1)} />
      <div className="screen">
        <p className="muted" style={{ fontSize: 13, marginTop: -4 }}>{t('guide.subtitle')}</p>

        <div className="guide-search">
          <Icon name="search" size={18} />
          <input
            value={query}
            placeholder={t('guide.searchPlaceholder')}
            onChange={(e) => setQuery(e.target.value)}
          />
          {query && (
            <button className="muted" style={{ background: 'none', border: 'none', cursor: 'pointer' }} onClick={() => setQuery('')}>
              <Icon name="close" size={16} />
            </button>
          )}
        </div>

        {results ? (
          results.length === 0 ? (
            <p className="muted" style={{ textAlign: 'center', padding: '20px 0' }}>{t('guide.noResults')}</p>
          ) : (
            <div className="stack" style={{ gap: 4 }}>
              {results.map((r, i) => (
                <Item key={i} tt={r.tt} dd={r.dd} />
              ))}
            </div>
          )
        ) : (
          <div className="stack" style={{ gap: 8 }}>
            {SECTIONS.map((s) => {
              const isOpen = open.has(s.key);
              return (
                <div key={s.key} className="guide-section">
                  <button className="guide-section__head" onClick={() => toggle(s.key)} aria-expanded={isOpen}>
                    <span className="guide-section__icon">
                      <Icon name={s.icon} size={18} />
                    </span>
                    <span className="guide-section__title">{t(`guide.${s.key}.title` as TranslationKey)}</span>
                    <span className={`guide-section__chev${isOpen ? ' guide-section__chev--open' : ''}`}>
                      <Icon name="chevron-right" size={18} />
                    </span>
                  </button>
                  {isOpen && (
                    <div className="guide-section__body">
                      {s.items.map((it) => (
                        <Item
                          key={it}
                          tt={t(`guide.${s.key}.${it}T` as TranslationKey)}
                          dd={t(`guide.${s.key}.${it}D` as TranslationKey)}
                        />
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
